// ABOUTME: The end of license purchase (DVLP-7860): purchased, or a failure that says
// ABOUTME: plainly whether trying again is safe. A charged payment never offers a way back.
import { observer } from 'mobx-react';
import { Button, CircleCheckIcon, Typography } from '@execonline-inc/execonline-ui';
import { L, NotTranslated, T } from '../../../../../Translations';
import licensePurchaseStore from '../Store';
import { Entry, Failed, Purchased } from '../Types';
import { Price } from './Common';

/** Stripe's id without its "pi_" prefix: what support looks up, and what the receipt shows. */
const orderReference = (stripePaymentIntentId: string): string =>
  stripePaymentIntentId.replace(/^pi_/, '');

/**
 * From the collection banner the buyer bought access to a catalog, so the close
 * sends them to find a course. From a course card they were trying to enroll in
 * that course, so the close names it and takes them straight to its enrollment.
 */
const NextStep = observer(function NextStep({ entry }: { entry: Entry }) {
  switch (entry.kind) {
    case 'collection': {
      return (
        <>
          <Typography className="mt-8" variant="bodyLarge" as="p" fontWeight="semibold">
            <T kind="Onwards and upwards." />
          </Typography>
          <div className="mt-4 w-full sm:w-80">
            <Button
              data-intercom-target="license-purchased-find-course-button"
              className="w-full"
              color="primary"
              size="md"
              onClick={licensePurchaseStore.finish}
            >
              <T kind="Find your first course" />
            </Button>
          </div>
        </>
      );
    }
    case 'course': {
      return (
        <>
          <Typography className="mt-8 max-w-md" variant="bodyLarge" as="p">
            <T
              kind="You're ready to enroll in <course/>."
              course={
                <Typography as="span" variant="bodyLarge" fontWeight="semibold">
                  <NotTranslated text={entry.courseTitle} />
                </Typography>
              }
            />
          </Typography>
          <div className="mt-4 w-full sm:w-80">
            <Button
              data-intercom-target="license-purchased-enroll-button"
              className="w-full"
              color="primary"
              size="md"
              onClick={() => {
                licensePurchaseStore.goToCourse(entry.experienceId);
              }}
            >
              <T kind="Continue to enrollment" />
            </Button>
          </div>
        </>
      );
    }
  }
});

export const LicensePurchased = observer(function LicensePurchased({ flow }: { flow: Purchased }) {
  const { offer } = flow;
  return (
    <div
      data-intercom-target="license-purchased"
      className="flex w-full flex-col items-center justify-center px-6 py-12 text-center md:px-16 md:py-0"
    >
      <CircleCheckIcon className="h-20 w-20" color="green" />
      <Typography className="mt-6 tracking-normal" variant="h4" as="h2" fontWeight="semibold">
        <T kind="Thanks for your purchase" />
      </Typography>
      <Typography className="mt-5" variant="subtitleSmall" as="p" fontWeight="semibold">
        <NotTranslated text={offer.name} />
      </Typography>
      <Typography className="mt-1" variant="bodyMedium" as="p" color="gray">
        <T
          kind="License active until <date/>"
          date={<L localizeable={offer.endsOn} format="long-month-day-year" />}
        />
      </Typography>
      <Typography className="mt-3" variant="bodyLarge" as="p" color="gray">
        <T
          kind="<price/> charged"
          price={
            <Typography as="span" variant="subtitleLarge" fontWeight="bold" color="dark">
              <Price cents={offer.priceInCents} />
            </Typography>
          }
        />
      </Typography>
      <Typography className="mt-6" variant="bodyMedium" as="p" color="gray">
        {licensePurchaseStore.receiptEmail
          .map((email) => (
            <T
              key="email"
              kind="Receipt sent to <email/>"
              email={
                <Typography as="span" variant="bodyMedium" color="dark">
                  <NotTranslated text={email} />
                </Typography>
              }
            />
          ))
          .getOrElse(() => (
            <T kind="Receipt sent to your email" />
          ))}
      </Typography>

      <NextStep entry={flow.entry} />

      <Typography className="mt-6 text-gray-500" variant="caption" as="p">
        <T
          kind="Order reference: {{reference}}"
          reference={orderReference(flow.stripePaymentIntentId)}
        />
      </Typography>
    </div>
  );
});

const FailureHeading = observer(function FailureHeading({ reason }: { reason: Failed['reason'] }) {
  switch (reason) {
    case 'could-not-start': {
      return <T kind="We could not start your purchase" />;
    }
    case 'not-confirmed': {
      return <T kind="Payment Not Confirmed" />;
    }
    case 'server-error': {
      return <T kind="We are experiencing an issue" />;
    }
  }
});

export const PurchaseFailed = observer(function PurchaseFailed({ flow }: { flow: Failed }) {
  return (
    <div
      data-intercom-target="license-purchase-failed"
      className="flex w-full flex-col items-center justify-center px-6 py-16 text-center md:px-16 md:py-0"
    >
      <Typography color="black" variant="h5" as="p" fontWeight="medium">
        <FailureHeading reason={flow.reason} />
      </Typography>
      <Typography className="mt-4 max-w-md text-gray-700" variant="bodyMedium" as="p">
        {flow.canGoBack ? (
          <T kind="Please try again, or contact support@execonline.com for assistance." />
        ) : (
          <T kind="Your payment was received, but we could not confirm your license yet. Please contact support@execonline.com and we will sort it out." />
        )}
      </Typography>
      <div className="mt-8 flex w-full flex-wrap justify-center gap-3">
        {flow.canGoBack && (
          <Button
            data-intercom-target="license-purchase-failed-back-button"
            color="primary"
            size="md"
            onClick={licensePurchaseStore.backToChoosing}
          >
            <T kind="Back to licenses" />
          </Button>
        )}
        <Button
          data-intercom-target="license-purchase-failed-close-button"
          color="gray"
          variant="outlined"
          size="md"
          onClick={licensePurchaseStore.close}
        >
          <T kind="Close" />
        </Button>
      </div>
    </div>
  );
});
